import {
  ChangeDetectionStrategy,
  Component,
  inject,
  linkedSignal,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { ActivatedRoute } from '@angular/router';
import { DualLoginUiService } from './dual-login.ui-service';
import { LoginComponent } from './login/login.component';

/**
 * Identifier-first login screen: the user enters their email or phone
 * number (#29713). Emails run realm discovery (Entra for corporate
 * domains, Auth0 otherwise); phone numbers always route to Auth0 —
 * Entra accounts are UPN/email-based. The matching redirect flow starts
 * with the identifier as the login hint. A "use UPS1 account instead"
 * escape covers users whose email looks corporate but who have no AD
 * account (field hands). Host apps route their unauthenticated landing
 * here and register `buildEntraMsalProviders(...)`; with no Entra
 * registration every identifier routes to Auth0 unchanged.
 */
@Component({
  selector: 'ups-dual-login',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, LoginComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DualLoginUiService],
  template: `
    <div class="login-page">
      @if (internalAuthLogin()) {
        <ups-internal-login
          (useDifferentProvider)="internalAuthLogin.set(false)"
        />
      } @else if (returningIssuer(); as issuer) {
        <div class="login-card">
          <h1 class="login-title">Welcome back</h1>
          <p class="login-subtitle">
            Continue with your
            {{ issuer === 'entra' ? 'Microsoft' : 'UPS1' }} account
          </p>

          <button
            kendoButton
            type="button"
            themeColor="primary"
            class="login-button"
            [disabled]="isRedirecting()"
            (click)="continueAsReturningUser(issuer)"
          >
            Continue
          </button>

          <button
            kendoButton
            type="button"
            fillMode="link"
            class="login-link"
            [disabled]="isRedirecting()"
            (click)="useDifferentAccount()"
          >
            Use a different account
          </button>

          @if (errorMessage()) {
            <p class="login-error" role="alert">{{ errorMessage() }}</p>
          }
        </div>
      } @else {
        <form class="login-card" (ngSubmit)="continueWithIdentifier()">
          <h1 class="login-title">Sign in</h1>
          <p class="login-subtitle">
            Enter your email address or phone number to continue
          </p>

          <input
            class="k-input k-input-md login-input"
            type="text"
            name="identifier"
            autocomplete="username"
            placeholder="you@universalplant.com or 555-123-4567"
            [(ngModel)]="identifier"
            [disabled]="isRedirecting()"
          />

          <button
            kendoButton
            type="submit"
            themeColor="primary"
            class="login-button"
            [disabled]="isRedirecting()"
          >
            Continue
          </button>

          <button
            kendoButton
            type="button"
            fillMode="link"
            class="login-link w-100"
            [disabled]="isRedirecting()"
            (click)="signInWithUps1()"
          >
            Sign in with a UPS1 account instead
          </button>

          @if (dualLogin.internalAvailable()) {
            <button
              kendoButton
              type="button"
              fillMode="link"
              class="login-link w-100"
              [disabled]="isRedirecting()"
              (click)="internalAuthLogin.set(true)"
            >
              Sign in with an internal account
            </button>
          }
          @if (errorMessage()) {
            <p class="login-error" role="alert">{{ errorMessage() }}</p>
          }
        </form>
      }
    </div>
  `,
  styles: [
    `
      :host::ng-deep {
        .login-page {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          background: var(--color-background, #f5f5f5);
        }
        .login-card {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          width: 22rem;
          padding: 2rem;
          border-radius: 0.5rem;
          background: var(--color-surface, #fff);
          box-shadow: 0 2px 12px rgb(0 0 0 / 0.12);
        }
        .login-title {
          margin: 0;
          font-size: 1.375rem;
        }
        .login-subtitle {
          margin: 0 0 0.5rem;
          color: var(--color-text-secondary, #666);
        }
        .login-input,
        .login-button {
          width: 100%;
        }
        .login-link {
          align-self: center;
        }
        .login-error {
          margin: 0.25rem 0 0;
          color: var(--color-error, #b00020);
          font-size: 0.875rem;
        }
        .login-success {
          margin: 0.25rem 0 0;
          color: #4fb000;
          font-size: 0.875rem;
        }
      }
    `,
  ],
})
export class DualLoginComponent {
  protected readonly dualLogin = inject(DualLoginUiService);
  private readonly route = inject(ActivatedRoute, { optional: true });

  identifier = '';
  readonly isRedirecting = signal(false);
  readonly errorMessage = signal('');

  /**
   * Returning users skip the email step (#29709): one click continues with
   * the remembered issuer. Deliberately NOT an auto-redirect — logout lands
   * here, and an unconditional redirect would make signing out impossible.
   */
  readonly returningIssuer = signal(this.dualLogin.returningIssuer());

  readonly internalAuthLogin = linkedSignal(
    () =>
      this.dualLogin.internalAvailable() &&
      this.returningIssuer() === 'internal'
  );

  constructor() {
    // A session that is already authenticated does not belong on the login
    // page — MSAL redirect logins land back HERE, not on the redirectUri
    // (#29713). Logged-out visitors report unauthenticated and stay.
    this.dualLogin.forwardAuthenticatedUser(this.returnTarget());
  }

  continueAsReturningUser(issuer: 'entra' | 'auth0'): void {
    this.errorMessage.set('');
    this.isRedirecting.set(true);
    try {
      if (issuer === 'entra') {
        this.dualLogin.loginWithEntra();
      } else {
        this.dualLogin.loginWithUps1(this.returnTarget());
      }
    } catch (err) {
      this.isRedirecting.set(false);
      this.errorMessage.set(
        err instanceof Error ? err.message : 'Sign-in failed to start.'
      );
    }
  }

  /** "Use a different account": forget the issuer, show the email form. */
  useDifferentAccount(): void {
    this.dualLogin.forgetIssuer();
    this.errorMessage.set('');
    this.returningIssuer.set(null);
  }

  private returnTarget(): string {
    return this.route?.snapshot.queryParamMap.get('returnTo') ?? '/';
  }

  continueWithIdentifier(): void {
    const identifier = this.identifier.trim();
    const kind = this.dualLogin.classifyIdentifier(identifier);
    if (!kind) {
      this.errorMessage.set('Please enter your email address or phone number.');
      return;
    }
    this.errorMessage.set('');
    this.isRedirecting.set(true);
    try {
      if (kind === 'phone') {
        // Phones are always Auth0 — Entra accounts are UPN/email-based.
        this.dualLogin.loginWithUps1(
          this.returnTarget(),
          this.dualLogin.normalizePhone(identifier)
        );
      } else if (this.dualLogin.discoverIssuer(identifier) === 'entra') {
        this.dualLogin.loginWithEntra(identifier);
      } else {
        this.dualLogin.loginWithUps1(this.returnTarget(), identifier);
      }
    } catch (err) {
      this.isRedirecting.set(false);
      this.errorMessage.set(
        err instanceof Error ? err.message : 'Sign-in failed to start.'
      );
    }
  }

  /** Field-hand escape: corporate-looking email but no AD account. */
  signInWithUps1(): void {
    this.errorMessage.set('');
    this.isRedirecting.set(true);
    try {
      this.dualLogin.loginWithUps1(
        this.returnTarget(),
        this.identifier.trim() || undefined
      );
    } catch (err) {
      this.isRedirecting.set(false);
      this.errorMessage.set(
        err instanceof Error ? err.message : 'Sign-in failed to start.'
      );
    }
  }
}
